import { GameStatus } from "./types/game-status";

declare global {
  interface Window {
    adsbygoogle: { [key: string]: unknown }[];
  }
}


export enum FontOptions {
  DEFAULT,
  COMIC_SANS,
  DYSLEXIA,
}

// NAMES ARE DEFINED HERE
export enum ColourScheme {
  CLASSIC,
  NOIR,
  SPARKLE,
  NEON,
  BLUE,
  NINETIES,
  PETAL,
  BLOOD,
  WORM,
  GOTH,
  HACKER,
  WINTER,
}

export enum PromptPack {
  BASIC,
  HARDCORE,
  FLUFF,
  ART,
  DRAMA,
  FANTASY,
  GAMERS,
  HUNGRY,
  PHRASES,
}

export enum Maturity {
  MATURE,
  ALL_AGES,
}

enum TextStyle {
  BASIC,
  ITALIC,
  BOLD,
}

// Types
type RoomDetails = {
  settings: {
    num_characters: number;
    write_duration: number;
    maturity: number;
    max_players: number;
    num_characters_select: number;  // Number of characters players must select
    num_switches: number;
    no_competition: boolean;
    show_instructions: boolean;
    hide_authors: boolean;

    moderation: boolean;
    self_shuffle: boolean;
    audiences: boolean;
    prompt_packs: PromptPack[];
  };
};

type RoomId = RoomDetails & {
  id: number;
  code: string;
  meta: RoomMeta;
};

/** Room mode selected when creating a room (UI/meta concept) */
export const RoomMode = {
  Standard: "standard",
  Otp: "otp",
  Solo: "solo",
  SelfInsert: "self_insert",
  BrokenTelephone: "broken_telephone",
} as const;
export type RoomMode = (typeof RoomMode)[keyof typeof RoomMode];

export type RoomMeta = {
  mode?: RoomMode;
};

export type Room = RoomId & {
  host: Player;
  extra: RoomExtra;
};

type RoomExtra = {
  num_players: number;
  num_audience: number;
  time_remaining: number;
};

export type PlayerDetails = {
  name: string;
  meta: PlayerMeta;
  secret?: string;
  user_id?: string;
};

type PlayerMeta = {
  sprite_id: number;
  mature: boolean;
  accessibility: boolean;
  accessibility2: number;
  needs_quickstart?: boolean;
};

export type PlayerId = PlayerDetails & {
  id: number;
  secret: string;
  room_id: number;
  room_code: string;
  game_id?: number;
};

export type Player = PlayerId & {
  data: PlayerData;
  /** String status - use this for status checks */
  status_str: GameStatus;
  current_story?: Story;
  current_submission?: StorySubmission;
  characters?: Character[];
  votes?: StoryVote[];
  submission?: SubmissionMeta;
  reactions?: Reaction[];
  index?: number;
  prompts?: Prompt[];
};

type PlayerData = {
  audience: boolean;
  points: number;
  vip: boolean;
  reaction_index?: number;
  reactions?: Map<string, string>;
  connected: boolean;
  premium: boolean;
};

type Reaction = {
  id: number;
  story_id: number;
  meta: ReactionMeta;
};

type ReactionMeta = {
  submission_reactions: number[][];
};

export type Game = {
  id: number;
  room_id: number;
  room: Room;
  host_id: number;
  players: Player[];
  /** @deprecated Use status_str instead */
  status: number;
  /** String status - use this for status checks */
  status_str: GameStatus;
  settings: GameSettings;
  submission?: SubmissionMeta;
  stories?: Story[];
  awards?: Award[];
  countdown: Countdown;
  game_stats: GameStats;
  extra: RoomExtra;
};

export type Countdown = {
  deadline: number;
};

type SubmissionMeta = {
  reading_start: number;
  submission_index: number;
  submission_start: number;
  submission_end: number;
};

export type GameSettings = {
  num_characters_submit: number;
  num_characters_select: number;  // Number of characters players must select
  write_duration: number;
  num_switches: number;
  show_instructions: boolean;
  maturity: Maturity;
  hide_authors: boolean;
  max_players: number;

  moderation: boolean;
  self_shuffle: boolean;
  audiences: boolean;
  multi_prompt: boolean;
  prompt_packs: PromptPack[];
  no_extra_reading_time: boolean;
  forced_prompts: boolean;
  character_maturity: boolean;
  allow_duplicate_characters: boolean;
  auto_sync_parts_to_players: boolean;
  // Mode flags (mutually exclusive for now)
  otp_mode?: boolean;
  self_insert_mode?: boolean;
  hidden_history?: boolean;
  solo_writing?: boolean;
  // Solo mode auto-set flags
  skip_voting?: boolean;
  skip_shuffle?: boolean;
};

export type DefaultGameSettings = Record<string, never>;

export type Character = {
  id: number;
  name: string;
  selected: boolean;
  meta: CharacterMeta;
};

type CharacterMeta = {
  locked: boolean;
  mature: boolean;
};

export type Story = {
  id: number;
  captain: Player;
  status: number;
  settings: StorySettings;
  characters?: Character[];
  content: string;
  submissions: StorySubmission[];
  order: number;
  shortcut: string;
  awards: Award[];
  meta: StoryMeta;
};

type StoryMeta = {
  index: number;
  points: number;
  title: string;
  awards: string[];
  reactions: Map<string, string>;
};

type StorySettings = {
  mature: boolean;
};

export type StorySubmission = {
  id: number;
  content: string;
  index: number;
  player_id: number;
  player: Player;
  meta: StorySubmissionMeta;
  prompts: string[];
};

type StorySubmissionMeta = {
  flagged: number[];
  censored: boolean;
  done: boolean;
  prompt: string;
};

type StoryDetails = {
  captain: Player;
  characters: Character[];
  settings: StorySettings;
};

export type TwitchStreamer = {
  login: string;
  display_name: string;
  profile_image_url: string;
};

export type Config = {
  maintenance: boolean;
  maintenance_message: string;
  notice_message: string;
};

export type FranticFanficUser = {
  rank: number;
  user_id: number;
  is_supporter?: boolean;
};

export type User = FranticFanficUser & {
  name: string;
  connection: string;
  premium: boolean;
  auth0Id: string;
};

export type StoryVote = {
  id: number;
  award: Award;
  story_id: number;
};

type Award = {
  id: number;
  meta: AwardMeta;
};

type AwardMeta = {
  name: string;
  subtitle: string;
  rule: number;
  points: number;
};

type GameStats = {
  meta: GameStatsMeta;
  audience_stats: AudienceStats;
};

type AudienceStats = {
  audience_count: number;
  audience_reacts: number;
};

type GameStatsMeta = {
  total_words: number;
  average_part_length: number;
  top_character_writer: string;
  top_character_count: number;
  bottom_character_writer: string;
  bottom_character_count: number;
  most_reacted: string;
  most_reacted_count: number;
  most_reactor: string;
  most_reactor_count: number;
  most_loved: string;
  most_words: string;
  least_words: string;
  selected: Map<string, string[]>;
};

export type GameMessage = {
  game_id: number;
  player_id: number;
  now: number;
  ack?: boolean;
  player?: Player;
  game?: Game;
  error?: string;
  goaway?: boolean;
};

export type PlayerMessage = {
  player_id: number;
  player_secret: string;
  game_id: number;
  player_action: PlayerAction;
};

export type PlayerAction = {
  code: string;
  body: any;
};

type PlayerUpdate = {
  player: Player;
  characters?: Character[];
  current_story?: Story;
  submission?: StorySubmission;
  flagged_submissions?: number[];
  done_submission?: boolean;
};

export type Prompt = {
  id: number;
  text: string;
};

export type UserPrompt = {
  id: number;
  prompt: string;
  prompt_pack_id: number;
};

export type UserPromptPack = {
  id: number;
  name: string;
};

type SubmissionContent = {};
